import { z } from 'zod';

import { type OffsetPaginationRequestQuery } from './pagination';
import { PrimaryKeySchema, commaDelimitedList } from './utils';

// File type enum for pipeline outputs
export const PipelineOutputFileTypeSchema = z.enum([
  'json',
  'parquet',
  'csv',
  'binary',
]);

export type PipelineOutputFileType = z.infer<
  typeof PipelineOutputFileTypeSchema
>;

// Compression type enum
export const CompressionTypeSchema = z.enum(['none', 'gzip', 'brotli', 'lz4']);

export type CompressionType = z.infer<typeof CompressionTypeSchema>;

/**
 * Schema for pipeline output versions which store references to large outputs in S3
 */
export const PipelineOutputVersionSchema = z.object({
  pipelineOutputVersionId: PrimaryKeySchema,
  pipelineExecutionId: PrimaryKeySchema,
  outputFileUri: z.string().describe('S3 URI where the output file is stored'),
  fileType: PipelineOutputFileTypeSchema.describe('Type of the output file'),
  fileSizeBytes: z
    .number()
    .int()
    .nonnegative()
    .nullable()
    .describe('Size of the output file in bytes'),
  fileHash: z
    .string()
    .nullable()
    .describe('Hash of the output file for integrity verification'),
  outputSummary: z
    .string()
    .nullable()
    .describe('Human-readable summary of the output'),
  compressionType: CompressionTypeSchema.nullable().describe(
    'Compression algorithm used for the file',
  ),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type PipelineOutputVersion = z.infer<typeof PipelineOutputVersionSchema>;

export const PipelineOutputVersionsSchema = z.array(
  PipelineOutputVersionSchema,
);

export type PipelineOutputVersions = z.infer<
  typeof PipelineOutputVersionsSchema
>;

export const CreatePipelineOutputVersionSchema =
  PipelineOutputVersionSchema.pick({
    pipelineExecutionId: true,
    outputFileUri: true,
    fileType: true,
    fileSizeBytes: true,
    fileHash: true,
    outputSummary: true,
    compressionType: true,
  });

export type CreatePipelineOutputVersion = z.infer<
  typeof CreatePipelineOutputVersionSchema
>;

export const UpdatePipelineOutputVersionSchema = z.object({
  outputSummary: z.string().optional(),
});

export type UpdatePipelineOutputVersion = z.infer<
  typeof UpdatePipelineOutputVersionSchema
>;

export const PipelineOutputVersionsQuery = z.object({
  pipelineOutputVersionIds: commaDelimitedList(
    PrimaryKeySchema.array(),
  ).optional(),
  pipelineExecutionIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
  fileType: PipelineOutputFileTypeSchema.optional(),
  compressionType: CompressionTypeSchema.optional(),
  minFileSizeBytes: z.number().int().nonnegative().optional(),
  maxFileSizeBytes: z.number().int().nonnegative().optional(),
});

export type PipelineOutputVersionsQuery = z.infer<
  typeof PipelineOutputVersionsQuery
> &
  OffsetPaginationRequestQuery<'createdAt' | 'updatedAt' | 'fileSizeBytes'>;
